import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Package,
  DollarSign,
  Menu,
  X,
  User,
  LogOut,
  Building2,
  Box,
  FileText,
  Landmark,
  Receipt,
  PanelLeftClose,
  PanelLeftOpen,
  TrendingUp,
  ShoppingCart,
  BarChart3,
  ReceiptText,
  SlidersHorizontal,
  Settings,
} from 'lucide-react';
import { logout } from '../utils/auth';
import { getUserRole, hasModuleAccess } from '../utils/rolePermissions';
import GlobalSearch from './GlobalSearch';

interface NavItem {
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
}

interface NavSection {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  dotColor: string;
  items: NavItem[];
}

interface UserData {
  id: number;
  name: string;
  email: string;
  phone_number: string | null;
  location: string | null;
  profile_picture: string | null;
  account_type: string | null;
  role: string;
  email_verified_at: string | null;
  created_at: string;
  updated_at: string;
}

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

const ALL_SECTIONS: NavSection[] = [
  {
    id: 'sales',
    label: 'Sales',
    icon: TrendingUp,
    color: 'text-blue-400',
    dotColor: 'bg-blue-500',
    items: [
      { name: 'Customers', icon: Users, path: '/customers' },
      { name: 'Quotations', icon: ReceiptText, path: '/quotations' },
      { name: 'Invoices', icon: ReceiptText, path: '/sales' },
      { name: 'Sales Transactions', icon: FileText, path: '/sales-transactions' },
      { name: 'Receive Payments', icon: DollarSign, path: '/receive-payments' },
    ],
  },
  {
    id: 'purchase',
    label: 'Purchase',
    icon: ShoppingCart,
    color: 'text-amber-400',
    dotColor: 'bg-amber-500',
    items: [
      { name: 'Suppliers', icon: Building2, path: '/suppliers' },
      { name: 'Purchase Orders', icon: ReceiptText, path: '/purchase-orders' },
      { name: 'Bills', icon: ReceiptText, path: '/purchases' },
      { name: 'Purchase Transactions', icon: FileText, path: '/purchase-transactions' },
      { name: 'Pay Bills', icon: DollarSign, path: '/pay-bills' },
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: Box,
    color: 'text-emerald-400',
    dotColor: 'bg-emerald-500',
    items: [
      { name: 'Products', icon: Package, path: '/products' },
      { name: 'Inventory', icon: Box, path: '/inventory' },
      { name: 'Inventory Transactions', icon: FileText, path: '/inventory-transactions' },
      { name: 'Inventory Adjustments', icon: SlidersHorizontal, path: '/inventory-adjustments' },
    ],
  },
  {
    id: 'accounts',
    label: 'Accounts & Reports',
    icon: BarChart3,
    color: 'text-violet-400',
    dotColor: 'bg-violet-500',
    items: [
      { name: 'Accounts', icon: Landmark, path: '/accounts' },
      { name: 'Reports', icon: FileText, path: '/reports' },
      { name: 'Settings', icon: Settings, path: '/settings' },
    ],
  },
];

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
  );
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [userData, setUserData] = useState<UserData | null>(null);

  const userRole = getUserRole();

  const visibleSections = ALL_SECTIONS.map(section => ({
    ...section,
    items: section.items.filter(item => {
      const module = item.path.replace('/', '');
      if (
        module === 'receive-payments' ||
        module === 'sales-transactions' ||
        module === 'quotations'
      ) {
        return hasModuleAccess(userRole, 'sales');
      }
      if (
        module === 'pay-bills' ||
        module === 'purchase-transactions' ||
        module === 'purchase-orders'
      ) {
        return hasModuleAccess(userRole, 'purchases');
      }
      if (module === 'inventory-transactions' || module === 'inventory-adjustments') {
        return hasModuleAccess(userRole, 'inventory');
      }
      if (module === 'settings') {
        return true;
      }
      return hasModuleAccess(userRole, module);
    }),
  })).filter(section => section.items.length > 0);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUserData(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;

  const toggleCollapsed = () => setCollapsed(prev => !prev);

  const showLabels = !collapsed;
  const sidebarWidth = collapsed ? 'lg:w-[4.5rem]' : 'lg:w-64';

  return (
    <>
      {/* Mobile menu button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2.5 rounded-lg text-white bg-slate-900 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-600 shadow-lg transition-colors"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
      >
        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 flex flex-col
          bg-slate-900 border-r border-slate-800
          shadow-xl lg:shadow-none
          transform transition-all duration-300 ease-in-out
          w-64 ${sidebarWidth}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 lg:static lg:inset-auto
        `}
      >
        {/* Brand header */}
        <div
          className={`flex border-b border-slate-800 min-h-[4rem] ${
            showLabels
              ? 'items-center justify-between px-4'
              : 'flex-col items-center justify-center gap-2 px-2 py-3'
          }`}
        >
          <div className={`flex items-center min-w-0 ${showLabels ? 'gap-3' : ''}`}>
            <div className="flex-shrink-0 w-9 h-9 bg-gradient-to-br from-slate-700 to-slate-800 rounded-lg flex items-center justify-center ring-1 ring-slate-600/50">
              <Receipt className="h-5 w-5 text-slate-200" />
            </div>
            {showLabels && (
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white leading-tight truncate">briX</p>
                <p className="text-[10px] font-medium text-slate-400 leading-tight truncate">
                  Invoicing System
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={toggleCollapsed}
              className="hidden lg:flex p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-md transition-colors"
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? (
                <PanelLeftOpen className="h-[18px] w-[18px]" />
              ) : (
                <PanelLeftClose className="h-[18px] w-[18px]" />
              )}
            </button>

            {showLabels && (
              <button
                onClick={() => setMobileOpen(false)}
                className="lg:hidden p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-md transition-colors"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2 space-y-1">
          {/* Search — always above Dashboard */}
          <div className={showLabels ? 'mb-1' : 'mb-0.5'}>
            <GlobalSearch collapsed={collapsed} />
          </div>

          {/* Dashboard always on top */}
          <div className={showLabels ? 'mb-2' : 'mb-1'}>
            {showLabels && (
              <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                Main
              </p>
            )}
            <NavButton
              item={{ name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' }}
              active={isActive('/dashboard')}
              collapsed={collapsed}
              onNavigate={handleNavigation}
            />
          </div>

          {/* Sectioned navigation */}
          {visibleSections.map((section, sIdx) => (
            <div key={section.id} className={sIdx > 0 ? 'pt-1' : ''}>
              {showLabels ? (
                <div className="flex items-center gap-2 px-3 mb-1.5 mt-2">
                  <section.icon className={`h-3 w-3 flex-shrink-0 ${section.color}`} />
                  <p className={`text-[10px] font-semibold uppercase tracking-widest ${section.color}`}>
                    {section.label}
                  </p>
                </div>
              ) : (
                <div className="flex justify-center my-2">
                  <div className={`w-5 h-px ${section.dotColor} opacity-40 rounded-full`} />
                </div>
              )}
              <ul className="space-y-0.5">
                {section.items.map(item => (
                  <li key={item.path}>
                    <NavButton
                      item={item}
                      active={isActive(item.path)}
                      collapsed={collapsed}
                      onNavigate={handleNavigation}
                      accentColor={section.dotColor}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* User profile */}
        <div className={`border-t border-slate-800 ${showLabels ? 'p-3' : 'p-2'}`}>
          <div
            className={`flex items-center rounded-lg bg-slate-800/50 ring-1 ring-slate-700/50 ${
              showLabels ? 'p-2.5 gap-3' : 'p-2 flex-col gap-2'
            }`}
          >
            <div className="flex-shrink-0 w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center ring-2 ring-slate-600/80 overflow-hidden">
              {userData?.profile_picture ? (
                <img
                  src={userData.profile_picture}
                  alt="Profile"
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <User className="h-4 w-4 text-slate-300" />
              )}
            </div>

            {showLabels && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {userData?.name || 'User'}
                </p>
                <p className="text-xs text-slate-400 truncate capitalize">
                  {userRole || 'No role'}
                </p>
              </div>
            )}

            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className={`
                flex-shrink-0 text-slate-400 hover:text-red-400 hover:bg-slate-700
                rounded-md transition-all duration-200
                ${showLabels ? 'p-1.5' : 'p-2'}
                ${isLoggingOut ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              title="Logout"
              aria-label="Logout"
            >
              {isLoggingOut ? (
                <div className="animate-spin h-4 w-4 border-2 border-slate-400 border-t-transparent rounded-full" />
              ) : (
                <LogOut className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
};

interface NavButtonProps {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  onNavigate: (path: string) => void;
  accentColor?: string;
}

const NavButton: React.FC<NavButtonProps> = ({ item, active, collapsed, onNavigate, accentColor = 'bg-slate-300' }) => {
  const showLabel = !collapsed;
  return (
    <a
      href={item.path}
      title={collapsed ? item.name : undefined}
      onClick={(e) => {
        // Let browser handle Ctrl/Cmd+click and middle-click natively (open new tab)
        if (e.ctrlKey || e.metaKey || e.button === 1) return;
        e.preventDefault();
        onNavigate(item.path);
      }}
      className={`
        group relative w-full flex items-center rounded-lg text-sm font-medium
        transition-all duration-200 cursor-pointer
        ${showLabel ? 'px-3 py-2.5 gap-3' : 'px-0 py-2.5 justify-center'}
        ${
          active
            ? 'bg-slate-800 text-white shadow-sm ring-1 ring-slate-700'
            : 'text-slate-400 hover:bg-slate-800/60 hover:text-slate-200'
        }
      `}
    >
      {active && (
        <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 ${accentColor} rounded-r-full`} />
      )}
      <item.icon
        className={`flex-shrink-0 h-[18px] w-[18px] ${
          active ? 'text-slate-200' : 'text-slate-500 group-hover:text-slate-300'
        }`}
      />
      {showLabel && <span className="truncate">{item.name}</span>}
    </a>
  );
};

export default Sidebar;
